import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload,
  Search,
  Filter,
  RefreshCw,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Clock,
  MessageSquare,
  ArrowUpDown,
  ChevronDown,
  Trash2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { apiFetch, API } from "@/utils/api";
import ReconciliationLinkDialog from "@/components/ReconciliationLinkDialog";
import ImportPreview from "@/components/ImportPreview";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileMovements from "@/mobile/MobileMovements";

function formatCurrency(amount) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-ES");
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-700",
    classified: "bg-blue-100 text-blue-700",
    reconciled: "bg-emerald-100 text-emerald-700",
    ignored: "bg-slate-100 text-slate-600",
  };

  const labels = {
    pending: "Pendiente",
    classified: "Clasificado",
    reconciled: "Conciliado",
    ignored: "Ignorado",
  };

  return (
    <Badge className={styles[status] || styles.pending} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

function NoteCell({ note, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note || "");

  if (editing) {
    return (
      <Input
        className="h-7 text-xs w-[180px]"
        maxLength={200}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => { onSave(value); setEditing(false); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { onSave(value); setEditing(false); }
          if (e.key === "Escape") { setValue(note || ""); setEditing(false); }
        }}
        autoFocus
      />
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setEditing(true)}
            className={`p-1 rounded hover:bg-slate-100 ${note ? "text-blue-500" : "text-slate-300"}`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs max-w-[200px]">{note || "Añadir nota..."}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function BankTransactionsDesktop() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [applyingRules, setApplyingRules] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [order, setOrder] = useState("desc");
  const [loadingMore, setLoadingMore] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  // A3 (mapeo asistido, 2026-07-10): CSV con cabeceras desconocidas → el
  // usuario asigna columna→campo; el backend recuerda el mapeo por banco.
  const [mappingState, setMappingState] = useState(null); // {file, columns, samples}
  const [mappingChoice, setMappingChoice] = useState({});
  const [applyingMapping, setApplyingMapping] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileInputRef = useRef(null);

  const PAGE_SIZE = 100;

  const fetchTransactions = async ({ append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("order", order);
      params.append("limit", PAGE_SIZE);
      params.append("skip", append ? transactions.length : 0);

      const response = await apiFetch(`${API}/bank/transactions?${params}`);

      if (response.ok) {
        const data = await response.json();
        const rows = data.transactions || [];
        setTransactions((prev) => (append ? [...prev, ...rows] : rows));
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Error al cargar movimientos");
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  // Estado y orden se aplican al instante; búsqueda y fechas, al pulsar "Filtrar".
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, order]);

  const handleCategoryChange = async (txId, field, value) => {
    try {
      const res = await apiFetch(`${API}/bank/transactions/${txId}`, {
        method: "PUT",
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTransactions(prev => prev.map(tx =>
          tx.transaction_id === txId ? { ...tx, ...updated } : tx
        ));
        toast.success("Actualizado");
      }
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  // Borrar un movimiento colado por error (extracto duplicado, etc.). El backend
  // rechaza los conciliados/clasificados (tienen asiento) — hay que deshacerlos antes.
  const handleDelete = async (tx) => {
    if (!window.confirm(
      `¿Borrar este movimiento?\n\n${tx.description || ""}\n` +
      `${tx.date?.slice(0, 10) || ""} · ${tx.amount} €\n\n` +
      `Se elimina de forma permanente. Úsalo solo para movimientos colados por ` +
      `error (p. ej. un extracto importado dos veces).`
    ))
      return;
    try {
      const res = await apiFetch(`${API}/bank/transactions/${tx.transaction_id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setTransactions(prev => prev.filter(t => t.transaction_id !== tx.transaction_id));
        setTotal(t => Math.max(0, t - 1));
        toast.success("Movimiento borrado");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo borrar el movimiento");
      }
    } catch (e) {
      toast.error("Error al borrar el movimiento");
    }
  };

  const handleSearch = () => {
    fetchTransactions();
  };

  // A3: plantilla CSV de ejemplo generada en cliente (mismas columnas que
  // acepta el parser genérico; la Fecha Valor es la que cuenta si existe).
  const downloadCsvTemplate = () => {
    const rows = [
      "Fecha;Fecha Valor;Concepto;Importe;Saldo",
      "01/06/2026;02/06/2026;Cobro factura cliente ejemplo;1.000,00;3.500,00",
      "10/06/2026;10/06/2026;Cuota autonomos;-294,00;3.206,00",
      "20/06/2026;21/06/2026;Suscripcion software;-29,99;3.176,01",
    ].join("\n");
    const blob = new Blob(["﻿" + rows], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_movimientos.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const uploadForPreview = async (file, colmap = null) => {
    const formData = new FormData();
    formData.append("file", file);
    if (colmap) formData.append("colmap", JSON.stringify(colmap));
    const response = await apiFetch(`${API}/bank/preview`, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || "Error al leer el archivo");
    }
    return response.json();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportDialogOpen(false);
    setImporting(true);
    setImportResult(null);
    try {
      const data = await uploadForPreview(file);
      if (data.needs_mapping) {
        // Banco no reconocido: abrir el asistente de mapeo de columnas
        setMappingChoice({});
        setMappingState({
          file,
          columns: data.columns_found || [],
          samples: data.sample_rows || [],
        });
      } else {
        setPreviewData(data);
      }
    } catch (err) {
      // El backend explica columnas encontradas vs esperadas: dar tiempo a leerlo
      toast.error(err.message || "Error de conexion", { duration: 12000 });
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const handleApplyMapping = async () => {
    if (!mappingState) return;
    setApplyingMapping(true);
    try {
      const colmap = {
        value_date: mappingChoice.value_date || null,
        booking_date: mappingChoice.booking_date || null,
        amount: mappingChoice.amount || null,
        description: mappingChoice.description ? [mappingChoice.description] : [],
        balance: mappingChoice.balance || null,
      };
      const data = await uploadForPreview(mappingState.file, colmap);
      if (data.needs_mapping) {
        throw new Error("El mapeo no produjo movimientos. Revisa las columnas elegidas.");
      }
      setMappingState(null);
      setPreviewData(data);
      toast.success("Mapeo aplicado — se recordará para próximas importaciones de este banco");
    } catch (err) {
      toast.error(err.message || "No se pudo aplicar el mapeo", { duration: 8000 });
    } finally {
      setApplyingMapping(false);
    }
  };

  const handleConfirmImport = async (transactions) => {
    setConfirming(true);
    try {
      const response = await apiFetch(`${API}/bank/import-confirmed`, {
        method: "POST",
        body: JSON.stringify({ transactions }),
      });
      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        setPreviewData(null);
        toast.success(`${result.imported} movimientos importados`);
        fetchTransactions();
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail || "Error al importar");
      }
    } catch (err) {
      toast.error("Error de conexion");
    } finally {
      setConfirming(false);
    }
  };

  const handleApplyRules = async () => {
    setApplyingRules(true);
    try {
      const response = await apiFetch(`${API}/bank/rules/apply`, {
        method: "POST",
      });

      if (response.ok) {
        const result = await response.json();
        const classified = result.classified ?? 0;
        const suggested = (result.matched ?? 0) - classified;
        if (classified === 0 && suggested === 0) {
          toast.info("Ninguna regla coincide con los movimientos pendientes");
        } else {
          // classified = con asiento hecho; suggested = regla sin cuenta,
          // queda preseleccionada en Conciliación para confirmar.
          const parts = [];
          if (classified > 0) parts.push(`${classified} clasificados con asiento`);
          if (suggested > 0) parts.push(`${suggested} con sugerencia para confirmar en Conciliación`);
          toast.success(parts.join(" · "));
        }
        fetchTransactions();
      }
    } catch (error) {
      toast.error("Error al aplicar reglas");
    } finally {
      setApplyingRules(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bank-transactions-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Movimientos Bancarios
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {total} movimientos en total
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleApplyRules}
            disabled={applyingRules}
            data-testid="apply-rules-btn"
          >
            {applyingRules ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Aplicar Reglas
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".csv,.xlsx,.xls,.pdf"
            className="hidden"
          />
          <Button
            onClick={() => setImportDialogOpen(true)}
            disabled={importing}
            data-testid="import-bank-btn"
          >
            {importing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importar (CSV/XLSX/PDF)
          </Button>
        </div>
      </div>

      {/* A3: pantalla previa al import — antes se abría el selector de archivos
          a pelo, sin decir qué formatos/bancos se soportan ni dar plantilla. */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="import-info-dialog">
          <DialogHeader>
            <DialogTitle>Importar movimientos del banco</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p className="font-medium text-slate-800">Formatos que entiende LiquidArc:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>BBVA</strong>: el Excel (.xlsx) o PDF de "Últimos movimientos" tal cual lo descargas.</li>
              <li><strong>CSV de tu banco</strong>: Santander, CaixaBank, Sabadell, ING, Openbank, Revolut y N26 (export en inglés incluido).</li>
              <li><strong>Cualquier otro CSV</strong> con una columna de fecha (Fecha / Fecha Valor / Date…) y una de importe (Importe / Amount…). Concepto y Saldo son opcionales.</li>
            </ul>
            <p className="text-xs text-slate-500">
              Si el archivo trae Fecha y Fecha Valor, se usa la <strong>Fecha Valor</strong> (la
              que cuenta para conciliar con el banco). Si algo no se reconoce, verás un aviso
              con las columnas encontradas y las esperadas.
            </p>
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button
              variant="outline"
              onClick={downloadCsvTemplate}
              data-testid="download-csv-template-btn"
            >
              Descargar plantilla CSV
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              data-testid="choose-import-file-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Elegir archivo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Result */}
      {importResult && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <FileSpreadsheet className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">
                  Importación completada
                </p>
                <p className="text-sm text-blue-700">
                  {importResult.imported} importados
                  {(importResult.duplicates ?? 0) > 0 ? `, ${importResult.duplicates} duplicados` : ""}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportResult(null)}
                className="ml-auto"
              >
                Cerrar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* A3: asistente de mapeo de columnas (banco no reconocido) */}
      <Dialog open={Boolean(mappingState)} onOpenChange={(v) => !v && setMappingState(null)}>
        <DialogContent className="max-w-2xl" data-testid="column-mapping-dialog">
          <DialogHeader>
            <DialogTitle>Tu banco no está en la lista — dinos qué es cada columna</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            No reconocemos las cabeceras de este archivo. Asigna cada campo a la
            columna que corresponda y LiquidArc <strong>recordará el mapeo</strong> para
            las próximas importaciones de este banco.
          </p>
          <div className="space-y-3">
            {[
              { key: "amount", label: "Importe *", required: true },
              { key: "value_date", label: "Fecha valor (la que cuenta)" },
              { key: "booking_date", label: "Fecha contable" },
              { key: "description", label: "Concepto / descripción" },
              { key: "balance", label: "Saldo" },
            ].map(({ key, label }) => (
              <div key={key} className="grid grid-cols-2 gap-3 items-center">
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <Select
                  value={mappingChoice[key] || "__none__"}
                  onValueChange={(v) =>
                    setMappingChoice((prev) => ({ ...prev, [key]: v === "__none__" ? null : v }))
                  }
                >
                  <SelectTrigger data-testid={`mapping-select-${key}`}>
                    <SelectValue placeholder="(sin asignar)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(sin asignar)</SelectItem>
                    {(mappingState?.columns || []).map((col) => {
                      const sample = mappingState?.samples?.[0]?.[col];
                      return (
                        <SelectItem key={col} value={col}>
                          {col}
                          {sample ? ` — ej: ${String(sample).slice(0, 24)}` : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            Obligatorio: el importe y al menos una fecha. Si el archivo trae una
            sola fecha, ponla en «Fecha valor».
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMappingState(null)}>
              Cancelar
            </Button>
            <Button
              data-testid="apply-mapping-btn"
              onClick={handleApplyMapping}
              disabled={
                applyingMapping ||
                !mappingChoice.amount ||
                (!mappingChoice.value_date && !mappingChoice.booking_date)
              }
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {applyingMapping ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Aplicar y previsualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Preview */}
      {previewData && (
        <Card>
          <CardContent className="p-4">
            <ImportPreview
              data={previewData}
              onConfirm={handleConfirmImport}
              onCancel={() => setPreviewData(null)}
              loading={confirming}
            />
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar en descripción..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="w-40">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Desde
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
            <div className="w-40">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Hasta
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
              />
            </div>
            <div className="w-44">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Estado
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="classified">Clasificado</SelectItem>
                  <SelectItem value="reconciled">Conciliado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={handleSearch} data-testid="filter-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
            <Button
              variant="outline"
              onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
              title="Cambiar orden por fecha"
              data-testid="order-toggle-btn"
            >
              <ArrowUpDown className="w-4 h-4 mr-2" />
              {order === "desc" ? "Más recientes" : "Más antiguos"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-dense min-w-[850px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Fecha</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right w-[120px]">Importe</TableHead>
                  <TableHead className="text-right w-[120px]">Saldo</TableHead>
                  <TableHead className="w-[60px]">Nota</TableHead>
                  <TableHead className="w-[100px]">Estado</TableHead>
                  <TableHead className="w-[50px]">Enlace</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No hay movimientos</p>
                      <p className="text-sm">Importa un archivo CSV de tu banco</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((tx) => (
                    <TableRow key={tx.transaction_id} data-testid={`tx-row-${tx.transaction_id}`}>
                      <TableCell className="font-mono text-sm">
                        {formatDate(tx.date)}
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">
                        {tx.description}
                      </TableCell>
                      <TableCell
                        className={`numeric ${tx.amount < 0 ? "negative" : "positive"}`}
                      >
                        {formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell className="numeric">
                        {tx.balance ? formatCurrency(tx.balance) : "-"}
                      </TableCell>
                      <TableCell>
                        <NoteCell
                          note={tx.note}
                          onSave={(val) => handleCategoryChange(tx.transaction_id, "note", val)}
                        />
                      </TableCell>
                      <TableCell>
                        {tx.category === "nominas" && tx.status === "pending" ? <Badge variant="outline">Pago de nómina · Sin conciliar</Badge> : <StatusBadge status={tx.status} />}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <ReconciliationLinkDialog kind="transaction" id={tx.transaction_id} />
                          {tx.status !== "reconciled" && !tx.reconciled_with && !tx.classified_account && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-red-600"
                              title="Borrar movimiento (colado por error)"
                              onClick={() => handleDelete(tx)}
                              data-testid={`delete-tx-${tx.transaction_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!loading && transactions.length > 0 && (
            <div className="flex items-center justify-between p-3 border-t border-slate-100">
              <span className="text-sm text-slate-500" data-testid="tx-count">
                Mostrando {transactions.length} de {total}
              </span>
              {transactions.length < total && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions({ append: true })}
                  disabled={loadingMore}
                  data-testid="load-more-btn"
                >
                  {loadingMore ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-2" />
                  )}
                  Cargar más
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function BankTransactions() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileMovements /> : <BankTransactionsDesktop />;
}
